import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Container,
  Divider,
  Button
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import jsPDF from 'jspdf';

const TerminosCondiciones = ({ privacyOnly = false }) => {
  const [hasReadToBottom, setHasReadToBottom] = useState(false);

  const generarPDF = () => {
    const doc = new jsPDF();
    
    // Configuración inicial
    doc.setFont('helvetica');
    doc.setFontSize(16);
    
    // Título
    doc.text('Términos y Condiciones de Uso - AI Symtomatix', 20, 20);
    doc.setFontSize(10);
    doc.text(`Última actualización: ${new Date().toLocaleDateString('es-CL')}`, 20, 30);
    
    // Contenido
    doc.setFontSize(12);
    let currentY = 40;
    const margen = 20;
    const interlineado = 7;
    
    // Función helper para agregar texto con salto de línea automático
    const agregarTexto = (texto, currentY) => {
      const lineas = doc.splitTextToSize(texto, 170);
      doc.text(lineas, margen, currentY);
      return currentY + (lineas.length * interlineado);
    };

    // Función para verificar y agregar nueva página si es necesario
    const verificarEspacio = (currentY, espacioNecesario = 40) => {
      if (currentY > 270 - espacioNecesario) {
        doc.addPage();
        return 20;
      }
      return currentY;
    };

    // 1. Introducción
    doc.setFontSize(14);
    currentY = agregarTexto('1. Introducción', currentY);
    doc.setFontSize(12);
    currentY = agregarTexto('Bienvenido a AI Symtomatix. Al acceder y utilizar esta aplicación, usted acepta estos términos y condiciones en su totalidad. No continúe usando esta aplicación si no acepta todos los términos y condiciones establecidos en este documento.', currentY + 5);

    // 2. Legislación Aplicable
    currentY = verificarEspacio(currentY + 10);
    doc.setFontSize(14);
    currentY = agregarTexto('2. Legislación Aplicable', currentY);
    doc.setFontSize(12);
    currentY = agregarTexto('Estos términos y condiciones se rigen por la legislación chilena, incluyendo pero no limitado a:', currentY + 5);
    currentY = agregarTexto('• Ley 19.628 sobre Protección de la Vida Privada', currentY + 5);
    currentY = agregarTexto('• Ley 20.584 sobre Derechos y Deberes de los Pacientes', currentY + 5);
    currentY = agregarTexto('• Ley 19.496 sobre Protección de los Derechos de los Consumidores', currentY + 5);
    currentY = agregarTexto('• Ley 20.120 sobre la Investigación Científica en el Ser Humano', currentY + 5);

    // 3. Protección de Datos Personales
    currentY = verificarEspacio(currentY + 10);
    doc.setFontSize(14);
    currentY = agregarTexto('3. Protección de Datos Personales', currentY);
    doc.setFontSize(12);
    currentY = agregarTexto('De acuerdo con la Ley 19.628, todos los datos personales y sensibles recopilados por la aplicación serán tratados con estricta confidencialidad. El usuario tiene derecho a:', currentY + 5);
    currentY = agregarTexto('• Acceder a sus datos personales', currentY + 5);
    currentY = agregarTexto('• Solicitar la modificación de sus datos', currentY + 5);
    currentY = agregarTexto('• Solicitar la eliminación o bloqueo de sus datos', currentY + 5);
    currentY = agregarTexto('• Oponerse a la utilización de sus datos para fines de publicidad o investigación', currentY + 5);

    // 4. Consentimiento Informado
    currentY = verificarEspacio(currentY + 10);
    doc.setFontSize(14);
    currentY = agregarTexto('4. Consentimiento Informado', currentY);
    doc.setFontSize(12);
    currentY = agregarTexto('En cumplimiento con la Ley 20.584:', currentY + 5);
    currentY = agregarTexto('• Toda atención médica será precedida por el consentimiento informado del paciente', currentY + 5);
    currentY = agregarTexto('• Se proporcionará información clara y comprensible sobre el diagnóstico y tratamiento', currentY + 5);
    currentY = agregarTexto('• El usuario tiene derecho a rechazar tratamientos y solicitar una segunda opinión', currentY + 5);

    // 5. Uso del Servicio
    currentY = verificarEspacio(currentY + 10);
    doc.setFontSize(14);
    currentY = agregarTexto('5. Uso del Servicio', currentY);
    doc.setFontSize(12);
    currentY = agregarTexto('La aplicación AI Symtomatix:', currentY + 5);
    currentY = agregarTexto('• No reemplaza la atención médica profesional presencial', currentY + 5);
    currentY = agregarTexto('• Es una herramienta de apoyo para el triaje inicial', currentY + 5);
    currentY = agregarTexto('• No debe utilizarse en casos de emergencia médica', currentY + 5);
    currentY = agregarTexto('• No garantiza la precisión absoluta de sus recomendaciones', currentY + 5);

    // 6. Responsabilidades del Usuario
    currentY = verificarEspacio(currentY + 10);
    doc.setFontSize(14);
    currentY = agregarTexto('6. Responsabilidades del Usuario', currentY);
    doc.setFontSize(12);
    currentY = agregarTexto('El usuario se compromete a:', currentY + 5);
    currentY = agregarTexto('• Proporcionar información veraz y actualizada', currentY + 5);
    currentY = agregarTexto('• Mantener la confidencialidad de sus credenciales de acceso', currentY + 5);
    currentY = agregarTexto('• No compartir su cuenta con terceros', currentY + 5);
    currentY = agregarTexto('• Reportar cualquier uso no autorizado de su cuenta', currentY + 5);
    currentY = agregarTexto('• Proporcionar una foto de perfil clara y actualizada como requisito obligatorio', currentY + 5);

    // 6.1 Foto de Perfil
    currentY = verificarEspacio(currentY + 10);
    doc.setFontSize(14);
    currentY = agregarTexto('6.1 Foto de Perfil', currentY);
    doc.setFontSize(12);
    currentY = agregarTexto('La foto de perfil es un requisito obligatorio para el uso de la aplicación:', currentY + 5);
    currentY = agregarTexto('• Debe ser una fotografía actual y clara del rostro del usuario', currentY + 5);
    currentY = agregarTexto('• Puede ser capturada directamente desde la cámara del dispositivo o subida desde la galería', currentY + 5);
    currentY = agregarTexto('• La aplicación proporciona acceso directo a la cámara del dispositivo para tomar la foto en el momento', currentY + 5);
    currentY = agregarTexto('• La foto debe mostrar claramente el rostro sin obstrucciones (sin lentes de sol, máscaras, etc.)', currentY + 5);
    currentY = agregarTexto('• La foto será utilizada para propósitos de identificación por el personal médico', currentY + 5);
    currentY = agregarTexto('• El usuario puede actualizar su foto de perfil en cualquier momento, manteniendo los mismos requisitos', currentY + 5);
    currentY = agregarTexto('• La foto será almacenada de forma segura siguiendo los mismos estándares de protección de datos personales', currentY + 5);
    currentY = agregarTexto('Requisitos técnicos de la foto:', currentY + 5);
    currentY = agregarTexto('• Formato: JPG, PNG o HEIC', currentY + 5);
    currentY = agregarTexto('• Resolución mínima: 640x480 píxeles', currentY + 5);
    currentY = agregarTexto('• Tamaño máximo: 5MB', currentY + 5);
    currentY = agregarTexto('• Iluminación adecuada que permita ver claramente el rostro', currentY + 5);
    currentY = agregarTexto('• Fondo preferentemente claro y uniforme', currentY + 5);

    // 6. Dispositivos Wearables y Datos de Salud
    currentY = verificarEspacio(currentY + 10);
    doc.setFontSize(14);
    currentY = agregarTexto('6. Dispositivos Wearables y Datos de Salud', currentY);
    doc.setFontSize(12);
    currentY = agregarTexto('AI Symtomatix integra datos de dispositivos wearables para mejorar la atención médica. Al utilizar esta funcionalidad:', currentY + 5);
    currentY = agregarTexto('• Usted autoriza la recopilación de datos biométricos de sus dispositivos wearables', currentY + 5);
    currentY = agregarTexto('• Los datos recopilados incluyen, pero no se limitan a: ritmo cardíaco, patrones de sueño, niveles de actividad física, presión arterial, niveles de oxígeno en sangre, temperatura corporal, y otros indicadores de salud', currentY + 5);
    currentY = agregarTexto('• Estos datos son procesados en tiempo real para proporcionar un mejor seguimiento de su salud', currentY + 5);
    currentY = agregarTexto('• Tiene derecho a revocar el acceso a sus datos de dispositivos wearables en cualquier momento', currentY + 5);
    currentY = agregarTexto('• Sus datos son encriptados y almacenados siguiendo estándares internacionales de seguridad', currentY + 5);
    currentY = agregarTexto('• Los datos son recopilados cada 5 minutos para asegurar un monitoreo continuo y efectivo', currentY + 5);
    currentY = agregarTexto('• Puede elegir qué tipos específicos de datos desea compartir desde sus dispositivos wearables', currentY + 5);
    currentY = agregarTexto('• Los datos históricos se mantienen por un período máximo de 12 meses', currentY + 5);
    currentY = agregarTexto('• Puede exportar sus datos en cualquier momento en un formato estándar', currentY + 5);
    currentY = agregarTexto('• Se le notificará sobre cualquier cambio en la política de recopilación de datos de dispositivos wearables', currentY + 5);
    currentY = agregarTexto('Beneficios del uso de datos wearables:', currentY + 5);
    currentY = agregarTexto('• Monitoreo continuo de signos vitales las 24 horas del día', currentY + 5);
    currentY = agregarTexto('• Detección temprana de anomalías en la salud mediante análisis de patrones', currentY + 5);
    currentY = agregarTexto('• Personalización de recomendaciones médicas basadas en datos reales', currentY + 5);
    currentY = agregarTexto('• Mejor seguimiento de tratamientos y recuperación', currentY + 5);
    currentY = agregarTexto('• Alertas automáticas en caso de detectar valores fuera de rango', currentY + 5);
    currentY = agregarTexto('• Generación de reportes detallados para su equipo médico', currentY + 5);
    currentY = agregarTexto('• Integración con su historial médico para un análisis más completo', currentY + 5);
    currentY = agregarTexto('Protección de datos de dispositivos wearables:', currentY + 5);
    currentY = agregarTexto('• Encriptación de extremo a extremo de todos los datos transmitidos', currentY + 5);
    currentY = agregarTexto('• Almacenamiento seguro en servidores certificados HIPAA', currentY + 5);
    currentY = agregarTexto('• Acceso restringido solo a personal médico autorizado', currentY + 5);
    currentY = agregarTexto('• Anonimización de datos para estudios estadísticos', currentY + 5);
    currentY = agregarTexto('• Cumplimiento con estándares internacionales de privacidad médica', currentY + 5);
    currentY = agregarTexto('• Auditorías regulares de seguridad y privacidad', currentY + 5);
    currentY = agregarTexto('• Protocolo de notificación en caso de brechas de seguridad', currentY + 5);

    // 7. Privacidad y Seguridad de Datos
    currentY = verificarEspacio(currentY + 10);
    doc.setFontSize(14);
    currentY = agregarTexto('7. Privacidad y Seguridad de Datos', currentY);
    doc.setFontSize(12);
    currentY = agregarTexto('Nos comprometemos a proteger su privacidad siguiendo los más altos estándares internacionales:', currentY + 5);
    currentY = agregarTexto('• Cumplimiento con GDPR y regulaciones locales de protección de datos', currentY + 5);
    currentY = agregarTexto('• Encriptación de extremo a extremo para todos los datos sensibles', currentY + 5);
    currentY = agregarTexto('• Almacenamiento en servidores seguros con certificación ISO 27001', currentY + 5);
    currentY = agregarTexto('• Acceso restringido y auditoría regular de seguridad', currentY + 5);
    currentY = agregarTexto('• Política de retención de datos transparente', currentY + 5);
    currentY = agregarTexto('• Derecho al olvido y portabilidad de datos', currentY + 5);

    // 8. Limitación de Responsabilidad
    currentY = verificarEspacio(currentY + 10);
    doc.setFontSize(14);
    currentY = agregarTexto('8. Limitación de Responsabilidad', currentY);
    doc.setFontSize(12);
    currentY = agregarTexto('AI Symtomatix:', currentY + 5);
    currentY = agregarTexto('• No se hace responsable por decisiones tomadas basadas únicamente en la información proporcionada por la aplicación', currentY + 5);
    currentY = agregarTexto('• Recomienda siempre consultar con un profesional de la salud para confirmación de diagnósticos', currentY + 5);
    currentY = agregarTexto('• Se reserva el derecho de modificar o interrumpir el servicio sin previo aviso', currentY + 5);

    // 9. Propiedad Intelectual
    currentY = verificarEspacio(currentY + 10);
    doc.setFontSize(14);
    currentY = agregarTexto('9. Propiedad Intelectual', currentY);
    doc.setFontSize(12);
    currentY = agregarTexto('Todos los derechos de propiedad intelectual sobre:', currentY + 5);
    currentY = agregarTexto('• El software', currentY + 5);
    currentY = agregarTexto('• El contenido', currentY + 5);
    currentY = agregarTexto('• Los algoritmos de IA', currentY + 5);
    currentY = agregarTexto('• La marca AI Symtomatix', currentY + 5);
    currentY = agregarTexto('Son propiedad exclusiva de AI Symtomatix y están protegidos por las leyes chilenas e internacionales de propiedad intelectual.', currentY + 7);

    // 10. Modificaciones de los Términos
    currentY = verificarEspacio(currentY + 10);
    doc.setFontSize(14);
    currentY = agregarTexto('10. Modificaciones de los Términos', currentY);
    doc.setFontSize(12);
    currentY = agregarTexto('AI Symtomatix se reserva el derecho de modificar estos términos en cualquier momento. Los cambios entrarán en vigor inmediatamente después de su publicación. Se notificará a los usuarios sobre cambios significativos en los términos y condiciones.', currentY + 5);

    // 11. Contacto
    currentY = verificarEspacio(currentY + 10);
    doc.setFontSize(14);
    currentY = agregarTexto('11. Contacto', currentY);
    doc.setFontSize(12);
    currentY = agregarTexto('Para cualquier consulta sobre estos términos y condiciones o asuntos relacionados con la plataforma, nuestro equipo de soporte está disponible a través de:', currentY + 5);
    currentY = agregarTexto('• Mesa de Ayuda: soporte@aisymtomatix.cl', currentY + 5);
    currentY = agregarTexto('• Asuntos Legales: legal@aisymtomatix.cl', currentY + 5);
    currentY = agregarTexto('• Protección de Datos: privacidad@aisymtomatix.cl', currentY + 5);
    currentY = agregarTexto('Horario de atención: Lunes a Viernes de 09:00 a 18:00 hrs.', currentY + 5);
    currentY = agregarTexto('Tiempo de respuesta máximo: 24 horas hábiles.', currentY + 5);

    // Nota final
    currentY = verificarEspacio(currentY + 10);
    doc.setFontSize(10);
    currentY = agregarTexto('Al utilizar AI Symtomatix, usted reconoce haber leído, entendido y aceptado estos términos y condiciones.', currentY + 5);

    // Guardar el PDF
    doc.save('Terminos_y_Condiciones_AI_Symtomatix.pdf');
  };

  const renderPrivacyContent = () => (
    <>
      <Box sx={{ mb: 4, maxHeight: '70vh', overflow: 'auto' }} onScroll={handleScroll}>
        <Typography variant="h6" gutterBottom>
          Privacidad y Seguridad de Datos
        </Typography>
        <Typography paragraph>
          Nos comprometemos a proteger su privacidad siguiendo los más altos estándares internacionales:
        </Typography>
        <Typography component="ul">
          <li>Cumplimiento con GDPR y regulaciones locales de protección de datos</li>
          <li>Encriptación de extremo a extremo para todos los datos sensibles</li>
          <li>Almacenamiento en servidores seguros con certificación ISO 27001</li>
          <li>Acceso restringido y auditoría regular de seguridad</li>
          <li>Política de retención de datos transparente</li>
          <li>Derecho al olvido y portabilidad de datos</li>
        </Typography>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Dispositivos Wearables y Datos de Salud
        </Typography>
        <Typography paragraph>
          AI Symtomatix integra datos de dispositivos wearables para mejorar la atención médica. Al utilizar esta funcionalidad:
        </Typography>
        <Typography component="ul">
          <li>Usted autoriza la recopilación de datos biométricos de sus dispositivos wearables</li>
          <li>Los datos recopilados incluyen, pero no se limitan a: ritmo cardíaco, patrones de sueño, niveles de actividad física, presión arterial, niveles de oxígeno en sangre, temperatura corporal, y otros indicadores de salud</li>
          <li>Estos datos son procesados en tiempo real para proporcionar un mejor seguimiento de su salud</li>
          <li>Tiene derecho a revocar el acceso a sus datos de dispositivos wearables en cualquier momento</li>
          <li>Sus datos son encriptados y almacenados siguiendo estándares internacionales de seguridad</li>
          <li>Los datos son recopilados cada 5 minutos para asegurar un monitoreo continuo y efectivo</li>
          <li>Puede elegir qué tipos específicos de datos desea compartir desde sus dispositivos wearables</li>
          <li>Los datos históricos se mantienen por un período máximo de 12 meses</li>
          <li>Puede exportar sus datos en cualquier momento en un formato estándar</li>
          <li>Se le notificará sobre cualquier cambio en la política de recopilación de datos de dispositivos wearables</li>
        </Typography>
        <Typography paragraph>
          Beneficios del uso de datos wearables:
        </Typography>
        <Typography component="ul">
          <li>Monitoreo continuo de signos vitales las 24 horas del día</li>
          <li>Detección temprana de anomalías en la salud mediante análisis de patrones</li>
          <li>Personalización de recomendaciones médicas basadas en datos reales</li>
          <li>Mejor seguimiento de tratamientos y recuperación</li>
          <li>Alertas automáticas en caso de detectar valores fuera de rango</li>
          <li>Generación de reportes detallados para su equipo médico</li>
          <li>Integración con su historial médico para un análisis más completo</li>
        </Typography>
        <Typography paragraph>
          Protección de datos de dispositivos wearables:
        </Typography>
        <Typography component="ul">
          <li>Encriptación de extremo a extremo de todos los datos transmitidos</li>
          <li>Almacenamiento seguro en servidores certificados HIPAA</li>
          <li>Acceso restringido solo a personal médico autorizado</li>
          <li>Anonimización de datos para estudios estadísticos</li>
          <li>Cumplimiento con estándares internacionales de privacidad médica</li>
          <li>Auditorías regulares de seguridad y privacidad</li>
          <li>Protocolo de notificación en caso de brechas de seguridad</li>
        </Typography>
      </Box>
    </>
  );

  const handleScroll = (e) => {
    const element = e.target;
    const isAtBottom = element.scrollHeight - element.scrollTop === element.clientHeight;
    setHasReadToBottom(isAtBottom);
  };

  return (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ p: 4, my: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {privacyOnly ? 'Política de Privacidad' : 'Términos y Condiciones de Uso'}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<PictureAsPdfIcon />}
            onClick={generarPDF}
            disabled={!hasReadToBottom}
            title={!hasReadToBottom ? "Debe leer todo el documento antes de poder descargarlo" : ""}
          >
            Descargar PDF
          </Button>
        </Box>
        
        <Typography variant="subtitle1" color="text.secondary" gutterBottom align="center">
          Última actualización: {new Date().toLocaleDateString('es-CL')}
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ maxHeight: '70vh', overflow: 'auto' }} onScroll={handleScroll}>
          {privacyOnly ? (
            renderPrivacyContent()
          ) : (
            <>
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>
                  1. Introducción
                </Typography>
                <Typography paragraph>
                  Bienvenido a AI Symtomatix. Al acceder y utilizar esta aplicación, usted acepta estos términos y condiciones en su totalidad. 
                  No continúe usando esta aplicación si no acepta todos los términos y condiciones establecidos en este documento.
                </Typography>
              </Box>

              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>
                  2. Legislación Aplicable
                </Typography>
                <Typography paragraph>
                  Estos términos y condiciones se rigen por la legislación chilena, incluyendo pero no limitado a:
                </Typography>
                <Typography component="ul">
                  <li>Ley 19.628 sobre Protección de la Vida Privada</li>
                  <li>Ley 20.584 sobre Derechos y Deberes de los Pacientes</li>
                  <li>Ley 19.496 sobre Protección de los Derechos de los Consumidores</li>
                  <li>Ley 20.120 sobre la Investigación Científica en el Ser Humano</li>
                </Typography>
              </Box>

              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>
                  3. Protección de Datos Personales
                </Typography>
                <Typography paragraph>
                  De acuerdo con la Ley 19.628, todos los datos personales y sensibles recopilados por la aplicación serán tratados con estricta confidencialidad. 
                  El usuario tiene derecho a:
                </Typography>
                <Typography component="ul">
                  <li>Acceder a sus datos personales</li>
                  <li>Solicitar la modificación de sus datos</li>
                  <li>Solicitar la eliminación o bloqueo de sus datos</li>
                  <li>Oponerse a la utilización de sus datos para fines de publicidad o investigación</li>
                </Typography>
              </Box>

              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>
                  4. Consentimiento Informado
                </Typography>
                <Typography paragraph>
                  En cumplimiento con la Ley 20.584:
                </Typography>
                <Typography component="ul">
                  <li>Toda atención médica será precedida por el consentimiento informado del paciente</li>
                  <li>Se proporcionará información clara y comprensible sobre el diagnóstico y tratamiento</li>
                  <li>El usuario tiene derecho a rechazar tratamientos y solicitar una segunda opinión</li>
                </Typography>
              </Box>

              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>
                  5. Uso del Servicio
                </Typography>
                <Typography paragraph>
                  La aplicación AI Symtomatix:
                </Typography>
                <Typography component="ul">
                  <li>No reemplaza la atención médica profesional presencial</li>
                  <li>Es una herramienta de apoyo para el triaje inicial</li>
                  <li>No debe utilizarse en casos de emergencia médica</li>
                  <li>No garantiza la precisión absoluta de sus recomendaciones</li>
                </Typography>
              </Box>

              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>
                  6. Responsabilidades del Usuario
                </Typography>
                <Typography paragraph>
                  El usuario se compromete a:
                </Typography>
                <Typography component="ul">
                  <li>Proporcionar información veraz y actualizada</li>
                  <li>Mantener la confidencialidad de sus credenciales de acceso</li>
                  <li>No compartir su cuenta con terceros</li>
                  <li>Reportar cualquier uso no autorizado de su cuenta</li>
                  <li>Proporcionar una foto de perfil clara y actualizada como requisito obligatorio</li>
                </Typography>
              </Box>

              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>
                  6.1 Foto de Perfil
                </Typography>
                <Typography paragraph>
                  La foto de perfil es un requisito obligatorio para el uso de la aplicación:
                </Typography>
                <Typography component="ul">
                  <li>Debe ser una fotografía actual y clara del rostro del usuario</li>
                  <li>Puede ser capturada directamente desde la cámara del dispositivo o subida desde la galería</li>
                  <li>La aplicación proporciona acceso directo a la cámara del dispositivo para tomar la foto en el momento</li>
                  <li>La foto debe mostrar claramente el rostro sin obstrucciones (sin lentes de sol, máscaras, etc.)</li>
                  <li>La foto será utilizada para propósitos de identificación por el personal médico</li>
                  <li>El usuario puede actualizar su foto de perfil en cualquier momento, manteniendo los mismos requisitos</li>
                  <li>La foto será almacenada de forma segura siguiendo los mismos estándares de protección de datos personales</li>
                </Typography>
                <Typography paragraph>
                  Requisitos técnicos de la foto:
                </Typography>
                <Typography component="ul">
                  <li>Formato: JPG, PNG o HEIC</li>
                  <li>Resolución mínima: 640x480 píxeles</li>
                  <li>Tamaño máximo: 5MB</li>
                  <li>Iluminación adecuada que permita ver claramente el rostro</li>
                  <li>Fondo preferentemente claro y uniforme</li>
                </Typography>
              </Box>

              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>
                  6. Dispositivos Wearables y Datos de Salud
                </Typography>
                <Typography paragraph>
                  AI Symtomatix integra datos de dispositivos wearables para mejorar la atención médica. Al utilizar esta funcionalidad:
                </Typography>
                <Typography component="ul">
                  <li>Usted autoriza la recopilación de datos biométricos de sus dispositivos wearables</li>
                  <li>Los datos recopilados incluyen, pero no se limitan a: ritmo cardíaco, patrones de sueño, niveles de actividad física, presión arterial, niveles de oxígeno en sangre, temperatura corporal, y otros indicadores de salud</li>
                  <li>Estos datos son procesados en tiempo real para proporcionar un mejor seguimiento de su salud</li>
                  <li>Tiene derecho a revocar el acceso a sus datos de dispositivos wearables en cualquier momento</li>
                  <li>Sus datos son encriptados y almacenados siguiendo estándares internacionales de seguridad</li>
                  <li>Los datos son recopilados cada 5 minutos para asegurar un monitoreo continuo y efectivo</li>
                  <li>Puede elegir qué tipos específicos de datos desea compartir desde sus dispositivos wearables</li>
                  <li>Los datos históricos se mantienen por un período máximo de 12 meses</li>
                  <li>Puede exportar sus datos en cualquier momento en un formato estándar</li>
                  <li>Se le notificará sobre cualquier cambio en la política de recopilación de datos de dispositivos wearables</li>
                </Typography>
                <Typography paragraph>
                  Beneficios del uso de datos wearables:
                </Typography>
                <Typography component="ul">
                  <li>Monitoreo continuo de signos vitales las 24 horas del día</li>
                  <li>Detección temprana de anomalías en la salud mediante análisis de patrones</li>
                  <li>Personalización de recomendaciones médicas basadas en datos reales</li>
                  <li>Mejor seguimiento de tratamientos y recuperación</li>
                  <li>Alertas automáticas en caso de detectar valores fuera de rango</li>
                  <li>Generación de reportes detallados para su equipo médico</li>
                  <li>Integración con su historial médico para un análisis más completo</li>
                </Typography>
                <Typography paragraph>
                  Protección de datos de dispositivos wearables:
                </Typography>
                <Typography component="ul">
                  <li>Encriptación de extremo a extremo de todos los datos transmitidos</li>
                  <li>Almacenamiento seguro en servidores certificados HIPAA</li>
                  <li>Acceso restringido solo a personal médico autorizado</li>
                  <li>Anonimización de datos para estudios estadísticos</li>
                  <li>Cumplimiento con estándares internacionales de privacidad médica</li>
                  <li>Auditorías regulares de seguridad y privacidad</li>
                  <li>Protocolo de notificación en caso de brechas de seguridad</li>
                </Typography>
              </Box>

              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>
                  7. Privacidad y Seguridad de Datos
                </Typography>
                <Typography paragraph>
                  Nos comprometemos a proteger su privacidad siguiendo los más altos estándares internacionales:
                </Typography>
                <Typography component="ul">
                  <li>Cumplimiento con GDPR y regulaciones locales de protección de datos</li>
                  <li>Encriptación de extremo a extremo para todos los datos sensibles</li>
                  <li>Almacenamiento en servidores seguros con certificación ISO 27001</li>
                  <li>Acceso restringido y auditoría regular de seguridad</li>
                  <li>Política de retención de datos transparente</li>
                  <li>Derecho al olvido y portabilidad de datos</li>
                </Typography>
              </Box>

              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>
                  8. Limitación de Responsabilidad
                </Typography>
                <Typography paragraph>
                  AI Symtomatix:
                </Typography>
                <Typography component="ul">
                  <li>No se hace responsable por decisiones tomadas basadas únicamente en la información proporcionada por la aplicación</li>
                  <li>Recomienda siempre consultar con un profesional de la salud para confirmación de diagnósticos</li>
                  <li>Se reserva el derecho de modificar o interrumpir el servicio sin previo aviso</li>
                </Typography>
              </Box>

              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>
                  9. Propiedad Intelectual
                </Typography>
                <Typography paragraph>
                  Todos los derechos de propiedad intelectual sobre:
                </Typography>
                <Typography component="ul">
                  <li>El software</li>
                  <li>El contenido</li>
                  <li>Los algoritmos de IA</li>
                  <li>La marca AI Symtomatix</li>
                </Typography>
                <Typography paragraph>
                  Son propiedad exclusiva de AI Symtomatix y están protegidos por las leyes chilenas e internacionales de propiedad intelectual.
                </Typography>
              </Box>

              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>
                  10. Modificaciones de los Términos
                </Typography>
                <Typography paragraph>
                  AI Symtomatix se reserva el derecho de modificar estos términos en cualquier momento. Los cambios entrarán en vigor inmediatamente después de su publicación.
                  Se notificará a los usuarios sobre cambios significativos en los términos y condiciones.
                </Typography>
              </Box>

              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>
                  11. Contacto
                </Typography>
                <Typography paragraph>
                  Para cualquier consulta sobre estos términos y condiciones o asuntos relacionados con la plataforma, nuestro equipo de soporte está disponible a través de:
                </Typography>
                <Typography component="ul">
                  <li>Mesa de Ayuda: soporte@aisymtomatix.cl</li>
                  <li>Asuntos Legales: legal@aisymtomatix.cl</li>
                  <li>Protección de Datos: privacidad@aisymtomatix.cl</li>
                </Typography>
                <Typography paragraph>
                  Horario de atención: Lunes a Viernes de 09:00 a 18:00 hrs.
                </Typography>
                <Typography paragraph>
                  Tiempo de respuesta máximo: 24 horas hábiles.
                </Typography>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Typography variant="body2" color="text.secondary" align="center">
                Al utilizar AI Symtomatix, usted reconoce haber leído, entendido y aceptado estos términos y condiciones.
              </Typography>
            </>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default TerminosCondiciones; 